import { Search, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';

import { useToast } from '@/src/components/Toast';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { trackEvent } from '@/src/lib/analytics';
import { getErrorMessage } from '@/src/utils/errors';

import { getTrendingGifs, searchGifs, type GiphyGif } from './giphy-service';

export function GiphyPickerModal({
  onClose,
  onSelect,
  visible,
}: {
  onClose: () => void;
  onSelect: (gif: GiphyGif) => void;
  visible: boolean;
}) {
  const { showError } = useToast();
  const { width } = useWindowDimensions();
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDesktopWeb = Platform.OS === 'web' && width >= 900;

  async function loadGifs(nextQuery = query) {
    setError(null);
    setLoading(true);

    try {
      trackEvent('giphy_search', {
        has_query: Boolean(nextQuery.trim()),
        query_length: nextQuery.trim().length,
      });
      const nextGifs = nextQuery.trim()
        ? await searchGifs(nextQuery)
        : await getTrendingGifs();
      setGifs(nextGifs);
    } catch (caughtError) {
      const message = getErrorMessage(
        caughtError,
        'Unable to load GIFs from GIPHY.',
      );
      setError(message);
      showError(caughtError, 'Unable to load GIFs from GIPHY.');
      setGifs([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!visible) {
      return;
    }

    void loadGifs('');
    trackEvent('giphy_picker_open');
    // Loading when the modal opens should ignore stale search text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable
          style={[
            styles.sheet,
            isDesktopWeb ? styles.desktopSheet : styles.mobileSheet,
          ]}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Choose a GIF</Text>
              <Text style={styles.attribution}>Powered by GIPHY</Text>
            </View>
            <Pressable
              accessibilityLabel="Close GIF picker"
              accessibilityRole="button"
              onPress={onClose}
              style={styles.closeButton}
            >
              <X color={colors.text} size={22} strokeWidth={2.4} />
            </Pressable>
          </View>

          <View style={styles.searchRow}>
            <TextInput
              autoCapitalize="none"
              maxLength={50}
              onChangeText={setQuery}
              onSubmitEditing={() => {
                void loadGifs();
              }}
              placeholder="Search GIFs"
              placeholderTextColor={colors.mutedText}
              returnKeyType="search"
              style={styles.searchInput}
              value={query}
            />
            <Pressable
              accessibilityLabel="Search GIFs"
              accessibilityRole="button"
              onPress={() => {
                void loadGifs();
              }}
              style={styles.searchButton}
            >
              <Search color={colors.surface} size={20} strokeWidth={2.4} />
            </Pressable>
          </View>

          {loading ? (
            <View style={styles.status}>
              <ActivityIndicator color={colors.primary} />
              <Text style={styles.statusText}>Loading GIFs...</Text>
            </View>
          ) : null}

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <FlatList
            contentContainerStyle={styles.gifGrid}
            data={gifs}
            keyExtractor={(gif) => gif.giphyId}
            keyboardShouldPersistTaps="handled"
            numColumns={2}
            renderItem={({ item }) => (
              <Pressable
                accessibilityLabel={`Select ${item.title}`}
                accessibilityRole="button"
                onPress={() => {
                  onSelect(item);
                  onClose();
                }}
                style={({ pressed }) => [
                  styles.gifTile,
                  pressed ? styles.gifTilePressed : null,
                ]}
              >
                <Image
                  resizeMode="contain"
                  source={{ uri: item.previewURL }}
                  style={[
                    styles.gifImage,
                    {
                      aspectRatio:
                        item.width > 0 && item.height > 0
                          ? item.width / item.height
                          : 1,
                    },
                  ]}
                />
              </Pressable>
            )}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  attribution: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    marginTop: spacing.xs,
    textTransform: 'uppercase',
  },
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.36)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  closeButton: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  error: {
    color: colors.danger,
    fontSize: 14,
    lineHeight: 20,
  },
  desktopSheet: {
    borderRadius: 18,
    maxHeight: 560,
    maxWidth: 620,
    width: '88%',
  },
  gifGrid: {
    gap: spacing.sm,
    paddingBottom: spacing.lg,
  },
  gifImage: {
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    width: '100%',
  },
  gifTile: {
    flex: 1,
    padding: spacing.xs,
  },
  gifTilePressed: {
    opacity: 0.76,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  searchButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 46,
    justifyContent: 'center',
    width: 46,
  },
  searchInput: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    flex: 1,
    fontSize: 16,
    minHeight: 46,
    paddingHorizontal: spacing.md,
  },
  searchRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  sheet: {
    backgroundColor: colors.surface,
    gap: spacing.md,
    overflow: 'hidden',
    padding: spacing.md,
  },
  mobileSheet: {
    borderRadius: 18,
    maxHeight: '72%',
    maxWidth: 620,
    width: '94%',
  },
  status: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  statusText: {
    color: colors.mutedText,
    fontSize: 14,
  },
  title: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
});
